import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import i18n from 'i18n';
import { RootState } from 'data/root.reducers';
import {
  Classes, Dialog, ProgressBar
} from '@blueprintjs/core';
import { SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { JurisdictionArea, JAreaAfRowData } from 'data/jurisdictionArea/jurisdictionArea.types';
import * as accessFunctionActions from 'data/accessFunction/accessFunction.actions';
import * as selectors from 'data/jurisdictionArea/jurisdictionArea.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/jurisdictionArea/jurisdictionArea.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import { FieldUpdateHandlerParam } from 'shared/form';
import { showSuccessMessage, showError } from 'shared/toaster';
import { Ref } from 'routes/JurisdictionArea/components/JurisdictionAreaForm/JurisdictionAreaForm';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormPanel from 'components/FormPanel';
import FormButtons from 'components/FormButtons';
import JurisdictionAreaDialog from '../../components/JurisdictionAreaDialog';
import JurisdictionAreaForm from '../../components/JurisdictionAreaForm';
import { jurisdictionAreaTemplate } from '../../templates/jurisdictionAreaTemplate';
import getJurisdictionAreaDataToCreate from '../../utils/getJurisdictionAreaDataToSubmit';
import { adaptTemplateVisibilityMode } from '../../utils/jurisdictionAreaTemplate.adapters';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isOpen: selectors.isJurisdictionAreaEditPopupOpen(state),
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  selectedJurisdictionArea: selectors.getSelectedJurisdictionArea(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
});

const mapDispatch = {
  closeJurisdictionAreaEditPage: actions.closeJurisdictionAreaEditPage,
  goToJurisdictionAreaList: actions.goToJurisdictionAreaList,
  getAccessFunctions: accessFunctionActions.getAccessFunctionsWithViewOptions,
  createJurisdictionArea: actions.createJurisdictionArea,
  deleteJurisdictionArea: actions.deleteJurisdictionArea,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const JurisdictionAreaEdit: FunctionComponent<Props> = ({
  deliveryFunctionId,
  selectedJurisdictionArea,
  history,
  isAuditEnabled,
  userId,
  userName,
  accessFunctions,
  closeJurisdictionAreaEditPage,
  goToJurisdictionAreaList,
  getAccessFunctions,
  createJurisdictionArea,
  deleteJurisdictionArea,
}: Props) => {
  const [template] = useState(adaptTemplateVisibilityMode(jurisdictionAreaTemplate, 'edit'));
  const [formData, setFormData] = useState<Partial<JurisdictionArea>>({});
  const [gridData, setGridData] = useState<JAreaAfRowData[]>([]);
  const [selectedRows, setSelectedRows] = useState<JAreaAfRowData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [progressBarValue, setProgressBarValue] = useState(0);
  const [accessFunctionPending, setAccessFunctionPending] = useState<string>('');
  const [failedAccessFunctions, setFailedAccessFunctions] = useState<string[]>([]);
  const [isJurisdictionAreaDialogOpen, setIsJurisdictionAreaDialogOpen] = useState<boolean>(false);
  const formRef = useRef<Ref>(null);
  const { t } = useTranslation();

  useEffect(() => {
    getAccessFunctions(deliveryFunctionId);
  }, [deliveryFunctionId]);

  useEffect(() => {
    const adaptedGridData: JAreaAfRowData[] = accessFunctions.map((accessFunction) => {
      return { id: accessFunction.id || '', AFTYPE: accessFunction.type, AFNAME: accessFunction.name }
    })
    setGridData(adaptedGridData);
  }, [accessFunctions]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    const clonedFormData = cloneDeep(formData);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const handleClickSave = () => {
    const isValid = formRef?.current?.isValid(true, true) ?? true;
    if (!isValid) {
      return;
    }

    const formValues = formRef?.current?.getValues();
    if (!formValues) {
      return;
    }

    let numAccessFunctionsHandled = 0;
    const accessFunctionsFailedToBeUpdated: string[] = [];
    setFailedAccessFunctions([]);
    setProgressBarValue(0);
    let allAccessFunctionsUpdated = true;

    const jurisdictionAreasToCreate = selectedRows.filter((selectedRow) => !selectedJurisdictionArea.find((accessFunctionPreviouslySelected) => accessFunctionPreviouslySelected.AFNAME === selectedRow.AFNAME));
    const jurisdictionAreasToDelete = selectedJurisdictionArea.filter((accessFunctionPreviouslySelected) => !selectedRows.find((selectedRow) => selectedRow.AFNAME === accessFunctionPreviouslySelected.AFNAME));

    if (jurisdictionAreasToCreate.length || jurisdictionAreasToDelete.length) {
      setIsLoading(true);
    }

    // Add access functions
    if (jurisdictionAreasToCreate.length) {
      setAccessFunctionPending(jurisdictionAreasToCreate[0].AFNAME);
      jurisdictionAreasToCreate.map(async (selectedRow, index) => {
        const body = getJurisdictionAreaDataToCreate(
          formValues,
          null,
          isAuditEnabled,
          userId,
          userName,
          selectedRow
        );
        const createRes = await createJurisdictionArea(deliveryFunctionId, body);
        if (createRes === undefined) {
          allAccessFunctionsUpdated = false;
          accessFunctionsFailedToBeUpdated.push(`${selectedRow.AFNAME} (${selectedRow.AFTYPE})`);
        }
        if (index === jurisdictionAreasToCreate.length - 1) {
          if (!jurisdictionAreasToDelete.length) {
            setIsLoading(false);
            if (allAccessFunctionsUpdated || (accessFunctionsFailedToBeUpdated.length < jurisdictionAreasToCreate.length)) {
              if (accessFunctionsFailedToBeUpdated.length) {
                setFailedAccessFunctions(accessFunctionsFailedToBeUpdated);
                setIsJurisdictionAreaDialogOpen(true);
              } else {
                goToJurisdictionAreaList(history);
                showSuccessMessage(i18n.t('jurisdictionArea.create.success', { areaName: formValues.SS8ID }), 5000);
              }
            } else {
              goToJurisdictionAreaList(history);
              showError(i18n.t('jurisdictionArea.create.failed', { areaName: body.data?.id, error: 'Error' }));
            }
          }

          return;
        }
        numAccessFunctionsHandled += 1;
        setProgressBarValue(numAccessFunctionsHandled / (jurisdictionAreasToCreate.length + jurisdictionAreasToDelete.length));
        setAccessFunctionPending(jurisdictionAreasToCreate[index + 1].AFNAME);
      });
    }

    // Remove access functions
    if (jurisdictionAreasToDelete.length) {
      setAccessFunctionPending(jurisdictionAreasToDelete[0].AFNAME);
      jurisdictionAreasToDelete.map(async (accessFunctionPreviouslySelected, index) => {
        const deleteRes = await deleteJurisdictionArea(deliveryFunctionId, accessFunctionPreviouslySelected.id)
        if (deleteRes === undefined) {
          allAccessFunctionsUpdated = false;
          accessFunctionsFailedToBeUpdated.push(`${accessFunctionPreviouslySelected.AFNAME} (${accessFunctionPreviouslySelected.AFTYPE})`);
        }
        if (index === jurisdictionAreasToDelete.length - 1) {
          setIsLoading(false);
          if (allAccessFunctionsUpdated || (accessFunctionsFailedToBeUpdated.length < (jurisdictionAreasToCreate.length + jurisdictionAreasToDelete.length))) {
            if (accessFunctionsFailedToBeUpdated.length) {
              setFailedAccessFunctions(accessFunctionsFailedToBeUpdated);
              setIsJurisdictionAreaDialogOpen(true);
            } else {
              goToJurisdictionAreaList(history);
              showSuccessMessage(i18n.t('jurisdictionArea.edit.success', { areaName: formValues.SS8ID }), 5000);
            }
          } else {
            goToJurisdictionAreaList(history);
            showError(i18n.t('jurisdictionArea.edit.failed', { areaName: formValues.SS8ID, error: 'Error' }));
          }
          return;
        }
        numAccessFunctionsHandled += 1;
        setProgressBarValue(numAccessFunctionsHandled / (jurisdictionAreasToCreate.length + jurisdictionAreasToDelete.length));
        setAccessFunctionPending(jurisdictionAreasToDelete[index + 1].AFNAME);
      });
    }
  }

  const closeJurisdictionAreaDialog = () => {
    setIsJurisdictionAreaDialogOpen(false);
    goToJurisdictionAreaList(history);
    const jurisdictionAreaName = formRef?.current?.getValues().SS8ID;
    showSuccessMessage(i18n.t('jurisdictionArea.edit.success', { areaName: jurisdictionAreaName }), 5000);
  }

  const handleCancel = () => closeJurisdictionAreaEditPage(history);

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    setSelectedRows(event.api.getSelectedRows());
  };

  const renderForm = () => {
    if (gridData && template) {
      return (
        <JurisdictionAreaForm
          ref={formRef}
          template={template}
          gridData={gridData}
          onSelectionChanged={handleSelectionChanged}
          onFieldUpdate={handleFieldUpdate}
          defaultsSelected={selectedJurisdictionArea}
          selectedJurisdictionArea={selectedJurisdictionArea}
          mode="edit"
        />
      )
    }
  };

  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="JurisdictionAreaEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('jurisdictionArea.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
      <Dialog
        title={t('jurisdictionArea.dialog.update.title')}
        isOpen={isLoading}
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
        isCloseButtonShown={false}
      >
        <div className={Classes.DIALOG_BODY}>
          <div className="jurisdiction-area-creating-access-functions">{t('jurisdictionArea.dialog.update.description', { accessFunctionPending })} {accessFunctionPending}...</div>
          <ProgressBar className=".jurisdiction-area-creating-access-functions-progress" intent="primary" value={progressBarValue} />
        </div>
      </Dialog>
      <JurisdictionAreaDialog
        title={t('jurisdictionArea.dialog.updated.title')}
        description={t('jurisdictionArea.dialog.updated.description')}
        isJurisdictionAreaDialogOpen={isJurisdictionAreaDialogOpen}
        accessFunctionsList={failedAccessFunctions}
        handleClose={closeJurisdictionAreaDialog}
      />
    </div>
  );
};

export default withRouter(connector(JurisdictionAreaEdit));
